/**
 * @fileoverview Column Actions Hook
 *
 * Internal hook used by UniversalTable to manage the lifecycle of column actions.
 * This hook handles executing before/after update handlers and provides the
 * necessary context to those handlers.
 *
 * @module UniversalTable/Hooks
 * @internal
 */

import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  ColumnActionConfig,
  ColumnActionContext,
  ColumnActionModalEvent,
  UseColumnActionsReturn,
  validateColumnActionConfigs
} from '../types/columnActions';

interface UseColumnActionsOptions {
  /** Array of column action configurations */
  columnActions?: ColumnActionConfig[];

  /** All rows in the table (for context) */
  rows: readonly any[];

  /** User session (optional, for context) */
  session?: any;

  /** Callback when a column action requests a modal */
  onModalRequest?: (event: ColumnActionModalEvent) => void;
}

/**
 * Hook for managing column action execution
 *
 * This hook provides a clean API for the table to execute column actions
 * at the appropriate lifecycle moments (before/after field updates).
 *
 * @param options - Configuration options
 * @returns Column action execution utilities
 *
 * @internal This hook is used internally by UniversalTable
 */
export function useColumnActions({
  columnActions = [],
  rows,
  session,
  onModalRequest
}: UseColumnActionsOptions): UseColumnActionsReturn {

  // Validate configs on mount (development only)
  useMemo(() => {
    if (process.env.NODE_ENV === 'development' && columnActions.length > 0) {
      try {
        validateColumnActionConfigs(columnActions);
      } catch (error) {
        console.error('[UniversalTable] Column action configuration error:', error);
      }
    }
  }, [columnActions]);

  // Build a lookup map for O(1) access to configs by field
  const actionsByField = useMemo(() => {
    const map = new Map<string, ColumnActionConfig>();

    // Use first config for each field (in case of duplicates)
    columnActions.forEach(config => {
      if (!map.has(config.field)) {
        map.set(config.field, config);
      }
    });

    return map;
  }, [columnActions]);

  /**
   * Build the context object passed to action handlers
   */
  const buildContext = useCallback((): ColumnActionContext => {
    return {
      openModal: (config) => {
        if (onModalRequest) {
          onModalRequest({ config });
        } else {
          console.warn(
            '[UniversalTable] Modal requested but no onModalRequest handler provided. ' +
            'Pass onModalRequest prop to UniversalTable to handle custom modals.'
          );
        }
      },

      showToast: (message, type = 'info') => {
        switch (type) {
          case 'success':
            toast.success(message);
            break;
          case 'error':
            toast.error(message);
            break;
          case 'warning':
            toast.warning(message);
            break;
          default:
            toast.info(message);
        }
      },

      allRows: rows,
      session
    };
  }, [rows, session, onModalRequest]);

  /**
   * Execute before-update actions for a field
   *
   * Returns false if any handler cancels the update.
   */
  const executeBeforeUpdateActions = useCallback(
    async (
      field: string,
      currentValue: any,
      newValue: any,
      rowIds: number[]
    ): Promise<boolean> => {
      const config = actionsByField.get(field);

      if (!config?.onBeforeUpdate) {
        return true; // No before-update handler, proceed
      }

      try {
        const context = buildContext();
        const result = await config.onBeforeUpdate(
          currentValue,
          newValue,
          rowIds,
          context
        );

        return result !== false; // Proceed unless explicitly false
      } catch (error) {
        console.error(
          `[UniversalTable] Error in onBeforeUpdate for field "${field}":`,
          error
        );

        // Default to allowing update even if handler errors
        // (prevents breaking the table due to bad action config)
        toast.error(`Error validating ${field} update`);
        return true;
      }
    },
    [actionsByField, buildContext]
  );

  /**
   * Execute after-update actions for a field
   *
   * Runs asynchronously after successful update.
   */
  const executeAfterUpdateActions = useCallback(
    async (
      field: string,
      value: any,
      rowIds: number[]
    ): Promise<void> => {
      const config = actionsByField.get(field);

      if (!config?.onAfterUpdate) {
        return; // No after-update handler, nothing to do
      }

      try {
        const context = buildContext();
        await config.onAfterUpdate(value, rowIds, context);
      } catch (error) {
        console.error(
          `[UniversalTable] Error in onAfterUpdate for field "${field}":`,
          error
        );

        // Don't re-throw - after-update failures shouldn't break the table
        // Just log and show a toast
        toast.error(`Error processing ${field} update`);
      }
    },
    [actionsByField, buildContext]
  );

  return {
    executeBeforeUpdateActions,
    executeAfterUpdateActions
  };
}
